import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import api from '../services/api';
import socket from '../services/socket';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

export function useDashboardData() {
    const { showToast } = useToast();
    const { user, tieneAccesoGlobal } = useAuth();

    const [citasHoy, setCitasHoy] = useState([]);
    const [loading, setLoading] = useState(true);

    // ️ FIX M5: leemos "¿lista vacía?" desde una ref, no desde la dependencia
    // citasHoy.length. Antes, cargarDatos cambiaba de identidad con cada variación
    // de la lista → el useEffect se limpiaba y re-suscribía los listeners del socket
    // y disparaba un fetch extra en bucle (fetch → setCitas → nueva identidad → fetch).
    const citasVaciasRef = useRef(true);
    useEffect(() => { citasVaciasRef.current = citasHoy.length === 0; }, [citasHoy]);

    // Fetch principal — identidad ESTABLE (solo depende de showToast)
    const cargarDatos = useCallback(async () => {
        try {
            if (citasVaciasRef.current) setLoading(true);
            const hoyStr = format(new Date(), 'yyyy-MM-dd');
            const res = await api.get(`/consultas/agenda?fecha=${hoyStr}`);
            setCitasHoy(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Error cargando la actividad del día:", err);
            showToast("No se pudo sincronizar la sala de espera", "error");
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Reactividad en tiempo real (Sockets)
    useEffect(() => {
        cargarDatos();
        if (!socket.connected) socket.connect();

        socket.on('nueva_cita_creada', cargarDatos);
        socket.on('estado_cita_actualizado', cargarDatos);

        return () => {
            socket.off('nueva_cita_creada', cargarDatos);
            socket.off('estado_cita_actualizado', cargarDatos);
        };
    }, [cargarDatos]);

    // Lógica de filtrado de negocio
    const citasFiltradas = useMemo(() => {
        let citasDeHoy = citasHoy.map(cita => {
            let horaCalculada = cita.hora || '';
            if (!horaCalculada && cita.fecha) {
                const d = new Date(cita.fecha);
                if (!isNaN(d)) horaCalculada = format(d, 'HH:mm');
            }
            return { ...cita, hora: horaCalculada || '--:--', estado: cita.estado || 'pendiente' };
        });

        if (!tieneAccesoGlobal) {
            citasDeHoy = citasDeHoy.filter(cita => {
                const idMedicoCita = cita.profesional?.id || cita.profesional;
                return String(idMedicoCita) === String(user?.id);
            });
        }

        return citasDeHoy.sort((a, b) => (a.hora || '00:00').localeCompare(b.hora || '00:00'));
    }, [citasHoy, tieneAccesoGlobal, user]);

    // Agrupación para la vista Multi-Sala de recepción
    const citasAgrupadasPorMedico = useMemo(() => {
        if (!tieneAccesoGlobal) return [];
        const agrupadas = {};
        citasFiltradas.forEach(cita => {
            const medicoId = cita.profesional?.id || cita.profesional || 'sin-asignar';
            const medicoNombre = cita.profesional?.nombre
                ? `${cita.profesional.nombre} ${cita.profesional.apellidos || ''}`
                : 'Médico Sin Asignar';

            if (!agrupadas[medicoId]) {
                agrupadas[medicoId] = { id: medicoId, nombre: medicoNombre, citas: [] };
            }
            agrupadas[medicoId].citas.push(cita);
        });
        return Object.values(agrupadas).sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [citasFiltradas, tieneAccesoGlobal]);

    // Cálculo de KPIs
    const metricas = useMemo(() => {
        const completadas = citasFiltradas.filter(c => c.estado === 'completada').length;
        const pendientes = citasFiltradas.filter(c => c.estado === 'pendiente' || c.estado === 'en_curso').length;
        return { total: citasFiltradas.length, completadas, pendientes };
    }, [citasFiltradas]);

    return {
        citasFiltradas,
        citasAgrupadasPorMedico,
        metricas,
        loading,
        cargarDatos,
        user,
        tieneAccesoGlobal
    };
}